import dayjs from 'dayjs';
// import { glCodes } from '../GLCodes';
import { useSelector } from 'react-redux';
import CardAccordion from '../CardAccordion';
import { useLocation } from 'react-router-dom';
import { constantFormValues } from '../../root';
import InputLabel from 'components/InputLabel/index';
import { useControlData } from '../LeaseUtils/helper';
import { filterOptionLabel } from 'pages/globals/global';
import { Col, DatePicker, Form, Row, Select, Empty } from 'antd';
import React, { Fragment, useEffect, useRef, useMemo } from 'react';
import { useGetQueryV3DeltaQuery } from 'store/features/api/graphSlice';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { glCodesShortForShow, glCodesToShow } from 'static-solutions/24155/identity-solution/general-ledger-code/GLUtility';

const AccountingInformation = ({ screen, identityId, leaseData, form }) => {
	const { state } = useLocation();

	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const [getAccountingAssignment] = useSendControlDataMutation();
	const { data: generalLedgerRes, isLoading: isGeneralLedgerListLoading } = useGetQueryV3DeltaQuery(
		{
			companyCode,
			fieldCodes: ['companyCode'],
			fieldValues: [companyCode],
			name: 'queryGLPostingAccountDynamic',
		},
		{ skipQuery: companyCode ? true : false }
	);

	const glOptions = useMemo(() => {
		return (
			generalLedgerRes?.results?.map((item) => {
				return { label: item?.displayName, value: item.propertyValue };
			}) || []
		);
	}, [generalLedgerRes]);

	const lastPayloadRef = useRef(null);

	function formatAccountingAssignmentData(inputData) {
		// Parse JSON if input is a string
		const data = typeof inputData === 'string' ? JSON.parse(inputData) : inputData;
		// Create output object
		const formattedOutput = {};
		// Process each key in the input data
		for (const key in data) {
			const entry = data[key];
			// Skip null entries
			if (entry === null) continue;

			// If the key is 'securityDeposit', use 'securityDepositName' as the output key
			if (key === 'securityDeposit') {
				formattedOutput['securityDepositName'] = `${entry.generalLedgerCode} - ${entry.generalLegerDescription}`;
			} else {
				formattedOutput[key] = `${entry.generalLedgerCode} - ${entry.generalLegerDescription}`;
			}
		}
		return formattedOutput;
	}
	const setAccountingAssignmentData = async (payload) => {
		// Skip API call if payload is missing data or data is null/undefined
		if (!payload || payload.data == null) {
			return;
		}
		// Compare new payload with the last one
		if (JSON.stringify(lastPayloadRef.current) === JSON.stringify(payload)) {
			// Payload is the same, skip API call
			return;
		}
		lastPayloadRef.current = payload; // Update last payload

		await getAccountingAssignment(payload).then((res) => {
			form.setFieldsValue({
				...formatAccountingAssignmentData(res?.data?.responseData),
			});
		});
	};
	useEffect(() => {
		if (screen?.toLowerCase() !== 'create' && identityId) {
			const payload = {
				data: identityId,
				processCode: state?.processCode,
				scriptName: 'lease-controller-accounts',
			};

			setAccountingAssignmentData(payload);
		}
	}, [identityId]);

	useEffect(() => {
		if (leaseData?.data) {
			form.setFieldsValue({
				gl2_rouAssets: leaseData?.data?.gl2_rouAssets,
				gl2_leaseLiabilityST: leaseData?.data?.gl2_leaseLiabilityST,
				gl2_leaseLiabilityLT: leaseData?.data?.gl2_leaseLiabilityLT,
				gl2_interestExpense: leaseData?.data?.gl2_interestExpense,
				gl2_leasePayables: leaseData?.data?.gl2_leasePayables,
				gl2_securityDeposit: leaseData?.data?.gl2_securityDeposit,
				gl2_depreciation: leaseData?.data?.gl2_depreciation,
				gl2_accumulatedDepreciation: leaseData?.data?.gl2_accumulatedDepreciation,
				gl2_gainFromLeases: leaseData?.data?.gl2_gainFromLeases,
				gl2_lossFromLeases: leaseData?.data?.gl2_lossFromLeases,

				gl_rouAssetInitialRecognition: leaseData?.data?.gl_rouAssetInitialRecognition,
				gl_rouAssetModification: leaseData?.data?.gl_rouAssetModification,
				gl_rouAssetPrepayments: leaseData?.data?.gl_rouAssetPrepayments,
				gl_rouAssetUpfrontPayment: leaseData?.data?.gl_rouAssetUpfrontPayment,
				gl_rouAssetIndirectCosts: leaseData?.data?.gl_rouAssetIndirectCosts,
				gl_rouCashIncentive: leaseData?.data?.gl_rouCashIncentive,
				gl_rouAssetKnockOff: leaseData?.data?.gl_rouAssetKnockOff,
				gl_securityDeposit: leaseData?.data?.gl_securityDeposit,
				gl_leaseLiabilityInitialRecognition: leaseData?.data?.gl_leaseLiabilityInitialRecognition,
				gl_leaseLiabilityModification: leaseData?.data?.gl_leaseLiabilityModification,
				gl_leaseLiabilityPayment: leaseData?.data?.gl_leaseLiabilityPayment,
				gl_leaseLiabilityInterest: leaseData?.data?.gl_leaseLiabilityInterest,
				gl_interestExpense: leaseData?.data?.gl_interestExpense,
				gl_depreciationRou: leaseData?.data?.gl_depreciationRou,
				gl_accumDepreciationRouDepreciation: leaseData?.data?.gl_accumDepreciationRouDepreciation,
				gl_accumDepreciationRouKnockOff: leaseData?.data?.gl_accumDepreciationRouKnockOff,
				gl_apControlLedgerPrepayments: leaseData?.data?.gl_apControlLedgerPrepayments,
				gl_apControlLedgerUpfrontPayment: leaseData?.data?.gl_apControlLedgerUpfrontPayment,
				gl_apControlLedgerIndirectCosts: leaseData?.data?.gl_apControlLedgerIndirectCosts,
				gl_apControlLedgerSecurityDeposit: leaseData?.data?.gl_apControlLedgerSecurityDeposit,
				gl_apControlLedgerPayment: leaseData?.data?.gl_apControlLedgerPayment,
				gl_arCashIncentive: leaseData?.data?.gl_arCashIncentive,
			});
		}
	}, [leaseData?.data]);

	return (
		<>
			<CardAccordion
				title="Reporting Information"
				customHeaderSX={{
					backgroundColor: '#e1f0ff !important',
					p: '10px',
				}}
				typographySX={{ fontSize: '16px !important' }}
				darkTitle={true}
				sx={{ mb: 2 }}
			>
				<Row gutter={[16, 0]}>
					<Col xs={4}>
						<InputLabel label={constantFormValues?.endOfFinancialYear?.label} head={true} />
					</Col>
					<Col xs={8}>
						<Form.Item
							name={constantFormValues?.endOfFinancialYear?.key}
							initialValue={dayjs().month(11).date(31)} // December 31st
							rules={[
								{
									required: false,
								},
							]}
						>
							<DatePicker
								allowClear={false}
								disabled
								format="DD MMMM" // Display format shows only month and day
								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								picker="date" // Still use date picker
								disabledDate={() => false} // Don't disable any dates based on year
								onChange={(date) => {
									if (date) {
										const currentYear = dayjs().year();
										const newDate = date.year(currentYear);
										form.setFieldsValue({
											[constantFormValues?.endOfFinancialYear?.key]: newDate,
										});
									}
								}}
							/>
						</Form.Item>
					</Col>
				</Row>
			</CardAccordion>

			<CardAccordion
				title="Accounting Information"
				customHeaderSX={{
					backgroundColor: '#e1f0ff !important',
					p: '10px',
				}}
				typographySX={{ fontSize: '16px !important' }}
				darkTitle={true}
				sx={{ mb: 2 }}
			>
				<Row gutter={[16, 0]}>
					{Object.entries(glCodesShortForShow).map(([label, key], index) => (
						<Fragment key={index}>
							<Col xs={4}>
								<InputLabel label={label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item
									name={key}
									// rules={[
									// 	{
									// 		required: true,
									// 		message: 'This filed is required',
									// 	},
									// ]}
								>
									<Select
										allowClear
										showSearch
										autoComplete="off"
										placeholder="Select from the list"
										// mode="multiple"
										// dropdownStyle={{ zIndex: 1301 }}
										styles={{
											popup: {
												root: { zIndex: 1301 },
											},
										}}
										// onSelect={(value, obj) => onSelectHandler(value, obj, key)}
										// onClear={() => onClearHandler(key)}
										filterOption={filterOptionLabel}
										options={glOptions}
										loading={isGeneralLedgerListLoading}
										disabled
										// notFoundContent={isGeneralLedgerListLoading ? null : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
									/>
								</Form.Item>
							</Col>
						</Fragment>
					))}
				</Row>
			</CardAccordion>

			<CardAccordion
				title="Accounting Information"
				customHeaderSX={{
					backgroundColor: '#e1f0ff !important',
					p: '10px',
				}}
				typographySX={{ fontSize: '16px !important' }}
				darkTitle={true}
				sx={{ mb: 2, display: 'none' }}
			>
				<Row gutter={[16, 0]}>
					{Object.entries(glCodesToShow).map(([label, key], index) => (
						<Fragment key={index}>
							<Col xs={4}>
								<InputLabel label={label} head={true} />
							</Col>
							<Col xs={8}>
								{/* <Form.Item name={`${key}Id`} noStyle />
								<Form.Item name={`${key}Type`} noStyle />
								<Form.Item name={`${key}Description`} noStyle /> */}
								<Form.Item name={key}>
									<Select
										allowClear
										showSearch
										placeholder="Select from the list"
										autoComplete="off"
										styles={{
											popup: {
												root: { zIndex: 1301 },
											},
										}}
										filterOption={filterOptionLabel}
										options={glOptions}
										loading={isGeneralLedgerListLoading}
										disabled
									/>
								</Form.Item>
							</Col>
						</Fragment>
					))}
				</Row>
			</CardAccordion>
		</>
	);
};

export default AccountingInformation;
